import type { Dispatch, SetStateAction } from 'react';
import {
  EMPTY_PALLET,
  EUR_PALLET,
  MAX_PLAUSIBLE_SIDE_CM,
  implausibleSides,
  type PalletDraft,
} from './pallet-draft';

interface PalletEditorProps {
  pallets: PalletDraft[];
  onChange: Dispatch<SetStateAction<PalletDraft[]>>;
  // Manual entry records Cargonizer's piece numbers; bookings get them back.
  showPieceNumber?: boolean;
  weightRequired?: boolean;
  // Weight and every dimension are required, e.g. for returns.
  measurementsRequired?: boolean;
  // Offer "Identical packages" and "Duplicate" for several packages of
  // the same size.
  allowCopies?: boolean;
  // Parcel carriers (Bring) ship boxes: say "Box" and drop the pallet size.
  boxes?: boolean;
}

/** Weight and dimensions per pallet, shared by outbound and return creation. */
export function PalletEditor({
  pallets,
  onChange,
  showPieceNumber,
  weightRequired,
  measurementsRequired = false,
  allowCopies = false,
  boxes = false,
}: PalletEditorProps) {
  const noun = boxes ? 'Box' : 'Pallet';
  function updatePallet(index: number, patch: Partial<PalletDraft>): void {
    onChange((current) => current.map((p, i) => (i === index ? { ...p, ...patch } : p)));
  }

  function addPallet(): void {
    onChange((current) => [...current, { ...EMPTY_PALLET }]);
  }

  function duplicatePallet(index: number): void {
    onChange((current) => [
      ...current.slice(0, index + 1),
      { ...(current[index] ?? EMPTY_PALLET), cargonizerPieceNumber: '' },
      ...current.slice(index + 1),
    ]);
  }

  function removePallet(index: number): void {
    onChange((current) => (current.length > 1 ? current.filter((_, i) => i !== index) : current));
  }

  return (
    <>
      {pallets.map((pallet, index) => (
        <div className="pallet-card" key={index}>
          <div className="pallet-card-heading">
            <span>
              {noun} {index + 1}
            </span>
            <span className="pallet-card-actions">
              {allowCopies ? (
                <button
                  type="button"
                  onClick={() => duplicatePallet(index)}
                  aria-label={`Duplicate ${noun.toLowerCase()} ${index + 1}`}
                >
                  Duplicate
                </button>
              ) : null}
              {pallets.length > 1 ? (
                <button type="button" onClick={() => removePallet(index)}>
                  Remove
                </button>
              ) : null}
            </span>
          </div>

          {showPieceNumber ? (
            <label>
              Piece number
              <input
                value={pallet.cargonizerPieceNumber}
                onChange={(event) =>
                  updatePallet(index, { cargonizerPieceNumber: event.target.value })
                }
              />
            </label>
          ) : null}

          <label>
            Weight
            <span className="pallet-field-with-unit">
              <input
                type="number"
                min="0"
                step="any"
                value={pallet.weightKg}
                required={weightRequired || measurementsRequired}
                onChange={(event) => updatePallet(index, { weightKg: event.target.value })}
                aria-label="Weight in kilograms"
              />
              <span className="drawer-version-note">kg</span>
            </span>
          </label>

          <label>
            Dimensions
            <div className="pallet-dimensions-row">
              <span className="pallet-dimension">
                <input
                  type="number"
                  min="0"
                  step="any"
                  value={pallet.lengthCm}
                  required={measurementsRequired}
                  onChange={(event) => updatePallet(index, { lengthCm: event.target.value })}
                  aria-label="Length in centimeters"
                />
                <span className="drawer-version-note">L</span>
              </span>
              <span className="pallet-dimension">
                <input
                  type="number"
                  min="0"
                  step="any"
                  value={pallet.widthCm}
                  required={measurementsRequired}
                  onChange={(event) => updatePallet(index, { widthCm: event.target.value })}
                  aria-label="Width in centimeters"
                />
                <span className="drawer-version-note">W</span>
              </span>
              <span className="pallet-dimension">
                <input
                  type="number"
                  min="0"
                  step="any"
                  value={pallet.heightCm}
                  required={measurementsRequired}
                  onChange={(event) => updatePallet(index, { heightCm: event.target.value })}
                  aria-label="Height in centimeters"
                />
                <span className="drawer-version-note">H</span>
              </span>
            </div>
            <span className="drawer-version-note">cm</span>
          </label>
          {boxes ? null : (
            <button
              type="button"
              className="pallet-preset"
              onClick={() => updatePallet(index, EUR_PALLET)}
              aria-label={`Set pallet ${index + 1} to 120 × 80 cm`}
            >
              Pallet 120 × 80
            </button>
          )}
          <ImplausibleSizeWarning pallet={pallet} />

          {allowCopies ? (
            <label className="pallet-copies">
              Identical packages
              <input
                type="number"
                min="1"
                step="1"
                inputMode="numeric"
                value={pallet.copies ?? ''}
                placeholder="1"
                onChange={(event) => updatePallet(index, { copies: event.target.value })}
                aria-label={`Identical packages like ${noun.toLowerCase()} ${index + 1}`}
              />
            </label>
          ) : null}
        </div>
      ))}
      <button type="button" onClick={addPallet}>
        Add {noun.toLowerCase()}
      </button>
    </>
  );
}

/** Flags sides over 3 m, which are almost always millimetres typed as centimetres. */
function ImplausibleSizeWarning({ pallet }: { pallet: PalletDraft }) {
  const sides = implausibleSides(pallet);
  if (sides.length === 0) return null;
  return (
    <p className="warning-callout" role="status">
      The {sides.join(' and ')} {sides.length > 1 ? 'are' : 'is'} over {MAX_PLAUSIBLE_SIDE_CM} cm.
      Check the unit: dimensions are in centimetres, not millimetres.
    </p>
  );
}
